/**
 * Damage scenario record schema — DataTable columns + FormModal
 * fields for Step 3.
 *
 * Real DB shape (from `damage_scenario_agent`):
 *   { damage_id, asset_ids: string[], compromised_properties: string[],
 *     damage_scenario, safety, financial, operational, privacy,
 *     final_impact_rating }
 *
 * Per ISO 21434, the SFOP impact dimensions (Safety / Financial /
 * Operational / Privacy) each use the four-level scale
 * Negligible / Moderate / Major / Severe. `final_impact_rating` is
 * the max of the four; the agent computes it.
 */
import React from 'react';
import LevelBadge from '../../../../components/ui/LevelBadge';
import { getImpactColor } from '../../../../utils/severityStyle';

export interface ScenarioRecord {
  damage_id: string;
  asset_ids?: string[];
  compromised_properties?: string[];
  damage_scenario?: string;
  safety?: string;
  financial?: string;
  operational?: string;
  privacy?: string;
  final_impact_rating?: string;
  [key: string]: unknown;
}

const joinList = (v: unknown): string =>
  Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v);

// ISO 21434 damage-scenario impacts use the four-tier scale
// Negligible / Moderate / Major / Severe. `getImpactColor` (V3 palette,
// `fourTier` scheme) is the canonical source of impact-rating colors
// — keeps Steps 3-7 consistent without re-defining a local tone map.
// Filled `LevelBadge` (not dot+text `StatusPill`) — impact reads as a
// swatch in the row, not a "status of this entity" label.
const renderImpactCell = (rating: unknown) =>
  rating ? (
    <LevelBadge
      color={getImpactColor(String(rating), { scheme: 'fourTier' })}
      label={String(rating)}
      size="sm"
    />
  ) : (
    ''
  );

const renderImpact = (row: ScenarioRecord) => renderImpactCell(row.final_impact_rating);
const renderSafety = (row: ScenarioRecord) => renderImpactCell(row.safety);
const renderFinancial = (row: ScenarioRecord) => renderImpactCell(row.financial);
const renderOperational = (row: ScenarioRecord) => renderImpactCell(row.operational);
const renderPrivacy = (row: ScenarioRecord) => renderImpactCell(row.privacy);

/** Column widths sum to 100% in read mode; edit mode reserves 4% for
 *  the trailing `_actions` kebab. Convention shared with Step 2.
 *  SFOP cells use single-letter headers (S / F / O / P) with title
 *  attributes for the full name — keeps the impact strip compact so
 *  the scenario description still gets the majority of the row width. */
export const SCENARIO_COLUMNS_READ = [
  { key: 'damage_id', label: 'ID', sortable: true, width: '7%' },
  {
    key: 'asset_ids',
    label: 'Assets',
    sortable: false,
    width: '12%',
    render: (row: ScenarioRecord) => joinList(row.asset_ids),
  },
  {
    key: 'compromised_properties',
    label: 'Compromised',
    sortable: false,
    width: '13%',
    render: (row: ScenarioRecord) => joinList(row.compromised_properties),
  },
  { key: 'damage_scenario', label: 'Scenario', sortable: false, width: '30%' },
  { key: 'safety',      label: 'S', headerTitle: 'Safety',      sortable: true, width: '7%', render: renderSafety },
  { key: 'financial',   label: 'F', headerTitle: 'Financial',   sortable: true, width: '7%', render: renderFinancial },
  { key: 'operational', label: 'O', headerTitle: 'Operational', sortable: true, width: '7%', render: renderOperational },
  { key: 'privacy',     label: 'P', headerTitle: 'Privacy',     sortable: true, width: '7%', render: renderPrivacy },
  {
    key: 'final_impact_rating',
    label: 'Impact',
    sortable: true,
    width: '10%',
    render: renderImpact,
  },
];

export const SCENARIO_COLUMNS_EDIT = [
  { key: 'damage_id', label: 'ID', sortable: true, width: '6%' },
  {
    key: 'asset_ids',
    label: 'Assets',
    sortable: false,
    width: '11%',
    render: (row: ScenarioRecord) => joinList(row.asset_ids),
  },
  {
    key: 'compromised_properties',
    label: 'Compromised',
    sortable: false,
    width: '12%',
    render: (row: ScenarioRecord) => joinList(row.compromised_properties),
  },
  { key: 'damage_scenario', label: 'Scenario', sortable: false, width: '30%' },
  { key: 'safety',      label: 'S', headerTitle: 'Safety',      sortable: true, width: '7%', render: renderSafety },
  { key: 'financial',   label: 'F', headerTitle: 'Financial',   sortable: true, width: '7%', render: renderFinancial },
  { key: 'operational', label: 'O', headerTitle: 'Operational', sortable: true, width: '7%', render: renderOperational },
  { key: 'privacy',     label: 'P', headerTitle: 'Privacy',     sortable: true, width: '7%', render: renderPrivacy },
  {
    key: 'final_impact_rating',
    label: 'Impact',
    sortable: true,
    width: '9%',
    render: renderImpact,
  },
];

export const SCENARIO_ACTIONS_COLUMN_WIDTH = '4%';

const SCENARIO_ID_PATTERN = /^[A-Z0-9_-]+$/;

const IMPACT_OPTIONS = [
  { value: 'Negligible', label: 'Negligible' },
  { value: 'Moderate', label: 'Moderate' },
  { value: 'Major', label: 'Major' },
  { value: 'Severe', label: 'Severe' },
];

/** FormModal field config. `asset_ids` options are injected at render
 *  time from the Step 2 asset list (passed via `ScenarioFormModal`'s
 *  `assetOptions` prop) since they vary per session. */
export const SCENARIO_FORM_FIELDS = [
  {
    name: 'damage_id',
    label: 'Damage ID',
    type: 'text',
    required: true,
    autoFocus: true,
    placeholder: 'e.g. DS-001',
    helpText: 'Unique identifier — uppercase letters, numbers, underscores, hyphens.',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (!s) return 'Damage ID is required';
      if (!SCENARIO_ID_PATTERN.test(s)) {
        return 'Damage ID must contain only uppercase letters, numbers, underscores, and hyphens';
      }
      return null;
    },
    width: 'half',
  },
  {
    name: 'final_impact_rating',
    label: 'Final Impact Rating',
    type: 'select',
    required: true,
    options: IMPACT_OPTIONS,
    // Derived: the highest of S/F/O/P. Editing the four inputs recalculates this
    // (and every risk value / CAL level derived from it). Setting it directly
    // detaches it from that formula, so it is an explicit override — hence the
    // warning rather than a silently-accepted value.
    helpText:
      'Derived from the highest of S / F / O / P — edit those and this recalculates. ' +
      'Setting it here overrides the formula for this scenario.',
    width: 'half',
  },
  {
    name: 'asset_ids',
    label: 'Affected Assets',
    type: 'multi-select',
    required: true,
    // Options injected by ScenarioFormModal at render time.
    options: [] as Array<{ value: string; label: string }>,
    helpText: 'Assets whose security properties this scenario compromises.',
    defaultValue: [],
  },
  {
    name: 'compromised_properties',
    label: 'Compromised Security Properties',
    type: 'checkbox-group',
    required: true,
    options: [
      { value: 'Confidentiality', label: 'Confidentiality' },
      { value: 'Integrity', label: 'Integrity' },
      { value: 'Availability', label: 'Availability' },
      { value: 'Authenticity', label: 'Authenticity' },
      { value: 'Non-repudiation', label: 'Non-repudiation' },
      { value: 'Authorization', label: 'Authorization' },
    ],
    defaultValue: [],
  },
  {
    name: 'damage_scenario',
    label: 'Scenario Description',
    type: 'textarea',
    required: true,
    rows: 5,
    placeholder:
      'Adverse consequence, how item functionality relates to it, and the resulting harm to road users.',
    helpText: 'Per ISO 21434: include the adverse consequence, the functionality link, and the road-user harm.',
    validate: (v: unknown) => {
      const s = String(v ?? '').trim();
      if (s.length < 50) {
        return 'Scenario must be at least 50 characters (adverse consequence + functionality + road-user harm).';
      }
      return null;
    },
  },
  {
    name: 'safety',
    label: 'Safety Impact',
    type: 'select',
    required: true,
    options: IMPACT_OPTIONS,
    width: 'half',
  },
  {
    name: 'financial',
    label: 'Financial Impact',
    type: 'select',
    required: true,
    options: IMPACT_OPTIONS,
    width: 'half',
  },
  {
    name: 'operational',
    label: 'Operational Impact',
    type: 'select',
    required: true,
    options: IMPACT_OPTIONS,
    width: 'half',
  },
  {
    name: 'privacy',
    label: 'Privacy Impact',
    type: 'select',
    required: true,
    options: IMPACT_OPTIONS,
    width: 'half',
  },
];
